import {
  type JobProgressionRealtimeEvent as JobProgressionRealtimeEventProto,
  GetJobProgressionRequest,
  OverrideJobProgressionStepRequest,
  SetAuthorizationRequestedProgressionRequest,
  SetAuthorizationSignedProgressionRequest,
  SetDeliverableCompletedProgressionRequest,
  SetDraftApprovedProgressionRequest,
  SetDraftInReviewProgressionRequest,
  SetDraftRejectedProgressionRequest,
  SetExtensionAcceptedProgressionRequest,
  SetExtensionConfirmedProgressionRequest,
  SetExtensionInProgressProgressionRequest,
  SetExtensionRejectedProgressionRequest,
  SetJobStartedProgressionRequest,
  SetOpenItemsProgressionRequest,
  SetPreparationCompletedProgressionRequest,
  SetReadyToFileProgressionRequest,
  SetReturnFiledProgressionRequest,
  SetReviewCompletedProgressionRequest,
} from '@taxfyle/api-internal/internal/job_progression_pb'
import { map, type Observable, share, Subject } from 'rxjs'
import { collection } from '@/infra/collection'
import { Id } from '@/infra/id'
import { filterNonNil } from '@/utils/rx-util'
import type { JobsApi } from '@/features/jobs/api/job-api'
import {
  mapEstimatedTaxPaymentToProto,
  mapExtensionProgressionFlowToProto,
  mapJobProgressionRealtimeEventProtoToProps,
  mapJobProgressionResponseProtoToProps,
  mapOverrideableJobProgressionStepStatusToProto,
} from './job-progression-mappers'
import {
  JobProgression,
  type JobProgressionTransition,
  type OverrideableJobProgressionStepStatus,
} from './JobProgression'

/**
 * Props used to prime a job progression in the store before it is fully loaded.
 */
export interface PrimeJobProgressionProps {
  jobId: Id
}

/**
 * Manages job progressions.
 */
export class JobProgressionStore {
  private readonly _updated = new Subject<Id>()

  /**
   * Tracked progressions by job ID.
   */
  readonly progressions = collection({
    create: () => new JobProgression(),
    getModelId: (p) => p.jobId,
    getDataId: (p) => p.jobId,
  })

  /**
   * IDs of the jobs whose progression was updated remotely.
   */
  readonly updated$: Observable<Id> = this._updated.asObservable()

  constructor(private readonly _api: JobsApi) {
    this._api.realtime$
      .pipe(
        map((event) => event.getJobProgression()),
        filterNonNil(),
        share(),
      )
      .subscribe(this._onRemoteEvent.bind(this))
  }

  /**
   * Primes the job progression.
   *
   * @param props
   */
  prime({ jobId }: PrimeJobProgressionProps): JobProgression {
    return this.progressions.get(jobId) ?? this.progressions.set({ jobId })
  }

  /**
   * Looks up the progression of a job.
   *
   * @param jobId
   */
  progressionForJob(jobId: Id): JobProgression | undefined {
    return this.progressions.get(jobId)
  }

  /**
   * Fetches the progression of a job.
   *
   * @param jobId
   * @param signal
   */
  async fetchProgression(jobId: Id, signal?: AbortSignal) {
    const id = Id.assert(jobId)
    const request = new GetJobProgressionRequest().setJobId(id)

    const response = await this._api.progressions.getJobProgression(request)
    signal?.throwIfAborted()

    this.progressions.set(mapJobProgressionResponseProtoToProps(response, id))
  }

  /**
   * Transitions the progression of a job to the next step.
   *
   * @param jobId
   * @param transition
   */
  async transitionToStep(jobId: Id, transition: JobProgressionTransition) {
    const id = Id.assert(jobId)
    const progressions = this._api.progressions
    switch (transition.step) {
      case 'JobStarted':
        await progressions.setJobStartedProgression(
          new SetJobStartedProgressionRequest().setJobId(id),
        )
        break
      case 'OpenItems':
        await progressions.setOpenItemsProgression(
          new SetOpenItemsProgressionRequest().setJobId(id),
        )
        break
      case 'DraftInReview':
        await progressions.setDraftInReviewProgression(
          new SetDraftInReviewProgressionRequest().setJobId(id),
        )
        break
      case 'DraftApproved':
        await progressions.setDraftApprovedProgression(
          new SetDraftApprovedProgressionRequest().setJobId(id),
        )
        break
      case 'DraftRejected':
        await progressions.setDraftRejectedProgression(
          new SetDraftRejectedProgressionRequest().setJobId(id),
        )
        break
      case 'PreparationCompleted':
        await progressions.setPreparationCompletedProgression(
          new SetPreparationCompletedProgressionRequest().setJobId(id),
        )
        break
      case 'ReviewCompleted':
        await progressions.setReviewCompletedProgression(
          new SetReviewCompletedProgressionRequest().setJobId(id),
        )
        break
      case 'AuthorizationRequested':
        await progressions.setAuthorizationRequestedProgression(
          new SetAuthorizationRequestedProgressionRequest().setJobId(id),
        )
        break
      case 'AuthorizationSigned':
        await progressions.setAuthorizationSignedProgression(
          new SetAuthorizationSignedProgressionRequest().setJobId(id),
        )
        break
      case 'ReturnFiled':
        await progressions.setReturnFiledProgression(
          new SetReturnFiledProgressionRequest().setJobId(id),
        )
        break
      case 'DeliverableCompleted':
        await progressions.setDeliverableCompletedProgression(
          new SetDeliverableCompletedProgressionRequest().setJobId(id),
        )
        break
      case 'ExtensionInProgress':
        await progressions.setExtensionInProgressProgression(
          new SetExtensionInProgressProgressionRequest()
            .setJobId(id)
            .setExtensionType(
              mapExtensionProgressionFlowToProto(transition.flow),
            ),
        )
        break
      case 'ExtensionConfirmed':
        await progressions.setExtensionConfirmedProgression(
          new SetExtensionConfirmedProgressionRequest().setJobId(id),
        )
        break
      case 'ExtensionAccepted': {
        const request = new SetExtensionAcceptedProgressionRequest().setJobId(
          id,
        )
        if (transition.estimatedTaxPayment) {
          request.setEstimatedTaxPayment(
            mapEstimatedTaxPaymentToProto(transition.estimatedTaxPayment),
          )
        }
        await progressions.setExtensionAcceptedProgression(request)
        break
      }
      case 'ExtensionRejected':
        await progressions.setExtensionRejectedProgression(
          new SetExtensionRejectedProgressionRequest().setJobId(id),
        )
        break
      case 'ReadyToFile':
        await progressions.setReadyToFileProgression(
          new SetReadyToFileProgressionRequest()
            .setJobId(id)
            .setSetAsAdmin(transition.setAsAdmin),
        )
        break
    }

    await this.fetchProgression(jobId)
  }

  /**
   * Overrides the current step of the progression of a job.
   *
   * @param jobId
   * @param step
   */
  async overrideProgressionStep(
    jobId: Id,
    step: OverrideableJobProgressionStepStatus,
  ) {
    const request = new OverrideJobProgressionStepRequest()
      .setJobId(Id.assert(jobId))
      .setProgressionStep(mapOverrideableJobProgressionStepStatusToProto(step))

    await this._api.progressions.overrideJobProgressionStep(request)
    await this.fetchProgression(jobId)
  }

  private _onRemoteEvent(event: JobProgressionRealtimeEventProto) {
    this.progressions.set(mapJobProgressionRealtimeEventProtoToProps(event))
    this._updated.next(event.getJobId())
  }
}
